import React from "react";
import { Link } from "react-router-dom";

/**
 * Avida Health logo — renders the full-color logo (navy house silhouette with
 * yellow flame + blue side-leaves + "avida" wordmark baked into the PNG).
 *
 * Sizes:
 *   - `xs`     → 24px icon-only (chips, inline mentions)
 *   - `sm`     → 32px tall
 *   - `md`     → 44px tall (page header)
 *   - `lg`     → 72px tall
 *   - `xl`     → 112px tall (landing hero)
 *   - `mega`   → 180px tall (hero watermark)
 *
 * Props:
 *   - iconOnly: when true, renders in a SQUARE container (so callers get a
 *     predictable aspect ratio for icon-chrome wrappers like rounded-card
 *     frames), but the full logo is object-contained inside — nothing is
 *     clipped. Previously this mode cropped ~5px from each side by
 *     oversizing the image behind `overflow-hidden`; that visual clip was
 *     the "top-left logo is cut off" bug.
 */
const HEIGHTS = {
  xs:   "h-6",
  sm:   "h-8",
  md:   "h-11",
  lg:   "h-[72px]",
  xl:   "h-[112px]",
  mega: "h-[180px]",
};

export default function AvidaLogo({
  size = "md",
  className = "",
  to,
  testid = "avida-logo",
  iconOnly = false,
  alt = "Avida Health",
}) {
  const heightClass = HEIGHTS[size] || HEIGHTS.md;

  // iconOnly keeps the square aspect (for icon-chrome containers) but
  // object-contains the FULL logo — no sides clipped.
  const img = iconOnly ? (
    <span
      className={`inline-block ${heightClass} aspect-square select-none ${className}`}
      data-testid={testid}
    >
      <img
        src="/brand/avida-logo.png"
        alt={alt}
        className="h-full w-full object-contain"
        draggable={false}
      />
    </span>
  ) : (
    <img
      src="/brand/avida-logo.png"
      alt={alt}
      className={`${heightClass} w-auto select-none ${className}`}
      draggable={false}
      data-testid={testid}
    />
  );

  if (to) {
    return (
      <Link to={to} className="inline-flex items-center" aria-label="Avida Health home">
        {img}
      </Link>
    );
  }
  return img;
}

/**
 * Thin blue brand bar used across page headers — shows the logo on
 * the left and an optional slot on the right. Use inside a sticky/top-of-page
 * container (e.g., Download Center, Submission Log, Agency Branding).
 */
export function AvidaBrandBar({ right, testid = "avida-brand-bar" }) {
  return (
    <div
      className="brand-strip-thin w-full h-16 px-6 flex items-center justify-between border-b border-white/10"
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-white p-1 border border-white/20">
          <AvidaLogo size="xs" to="/" iconOnly />
        </div>
        <div className="pl-3 border-l border-white/20">
          <div className="font-display text-[14px] font-semibold leading-tight text-white">Avida Health</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFDA0B]">NSA Workflow</div>
        </div>
      </div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );
}
